import { ContentSection } from '../components/content-section';
import { Layout } from '../components/page-layout';
import Privacy1 from '../assets/privacy-policy-1.png';
import Privacy2 from '../assets/privacy-policy-2.png';
import { Picture } from '../components/picture';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
`;
const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: bold;
`;
const SubText = styled.div`
  font-size: 0.875rem;
  color: #4b5563;
  margin-top: 1.5rem;
`;
const MainParagraph = styled.p`
  margin-top: 2rem;
  color: #7A7A7A;
`;
const FlexRow = styled.div`
  display: flex;
  flex-direction: column;
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;
const PictureCol = styled.div`
  margin-top: 3.5rem;
  flex-shrink: 0;
  margin-bottom: 2rem;
  @media (min-width: 768px) {
    width: 548px;
  }
`;

const TextCol = styled.div`
    margin-top: 3.5rem;
    @media (min-width: 768px) {
    margin-left: 2rem;
  }
`;

const HighlightSection = styled.div`
  padding: 1.5rem;
  margin-top: 3rem;
  background: #F87B15;
  color: #fff;
  border-radius: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const HighlightText = styled.p`
  text-align: center;
  color: #fff;
  line-height: 1.5;
`;


const HighlightButton = styled.a`
  font-family: 'Nunito', sans-serif;
  background: #fff;
  color: #F87B15;
  border-radius: 9999px;
  padding: 0.5rem 2rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  font-size: 1rem;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #ffe3d0;
    color: #d65c00;
  }
`;

export default function PrivacyPolicy() {
  return (
    <Layout showCommonFooter={true}>
      <Container>
        <Title>Privacy Policy for Sensforge</Title>
        <SubText>Effective Date: Jan 1st 2025</SubText>
        <MainParagraph>
          Sensforge Inc. (“Sensforge,” “we,” or “us”) respects your privacy and
          is committed to protecting your personal information. This Privacy
          Policy explains how we collect, use, and share your information when
          you use our products, applications, websites, and services
          (collectively, “Services”). By accessing the Services, you acknowledge
          that you have agreed to our process of your information in accordance
          with this Privacy Policy.
        </MainParagraph>
        <FlexRow>
          <ContentSection title="1. Information We Collect" className="mt-6">
            <p>We may collect the following types of information:</p>
            <ul>
              <li>Personal Information: Information you provide directly, such as your name, email address, payment details, and phone number.</li>
              <li>Device Information: Information about your device and usage, including IP addresses, device identifiers, and operating system.</li>
              <li>Automated Data: Information collected through cookies and similar technologies to track usage patterns and enhance your experience.</li>
            </ul>
          </ContentSection>
          <PictureCol>
            <Picture src={Privacy1} />
          </PictureCol>
        </FlexRow>
        <ContentSection title="2. How We Use Your Information" className="mt-14">
          <p>We use the collected information to:</p>
          <ul>
            <li>Provide and improve our Services, including customer support and updates.</li>
            <li>Enhance security and prevent unauthorized use.</li>
            <li>Personalize your experience with relevant content and features.</li>
            <li>Communicate promotional offers, subject to your preferences.</li>
            <li>Comply with legal obligations.</li>
          </ul>
        </ContentSection>
        <ContentSection title="3. Information Sharing" className="mt-14">
          <p>
            We do not sell your personal information. We may share your information with:
          </p>
          <ul>
            <li>Service Providers: For data storage, analytics, payment processing, or customer support.</li>
            <li>Business Partners: With your consent, to enable specific integrations or features.</li>
            <li>Legal Authorities: As required by law or a court order or other binding decision to protect our rights.</li>
          </ul>
        </ContentSection>
        <FlexRow>
          <PictureCol>
            <Picture src={Privacy2} />
          </PictureCol>
          <TextCol>
          <ContentSection title="4. Cookies and Tracking Technologies" className="mt-14">
            <p>
              We use cookies and similar technologies to enhance your browsing experience, gather analytics, and provide tailored advertisements. You can manage your preferences via our Cookie Preferences Manager or browser settings.
            </p>
          </ContentSection>
          </TextCol>
        </FlexRow>
        <ContentSection title="5. Your Choices" className="mt-14">
          <p>You have control over your data, including the ability to:</p>
          <ul>
            <li>Opt-out of promotional emails or text messages.</li>
            <li>Adjust cookie preferences and third-party analytics settings.</li>
            <li>Request access, updates, or deletion of your data where legally applicable.</li>
            <li>Object to our processing of your data or withdraw your consent at any time.</li>
          </ul>
        </ContentSection>
        <ContentSection title="6. Data Security" className="mt-14">
          <p>
            We employ industry-standard security measures to protect your data. However, no system can guarantee 100% security, and you use our Services at your own risk.
          </p>
        </ContentSection>
        <ContentSection title="7. Children's Privacy" className="mt-14">
          <p>
            Our Services are not intended for children under 18 or equivalent minimum age in the relevant jurisdiction, and we do not knowingly collect their data. By using the Services, you represent that you are at least 18 years old or above the equivalent minimum age in the relevant jurisdiction. If we have collected or received personal information from a child under 18 or equivalent minimum age in the relevant jurisdiction without verification of parental consent, we will promptly delete such information. If you are a parent or guardian of a child under the age of 18 or equivalent minimum age in the relevant jurisdiction and believe he or she has disclosed personal information to us, please <a href="mailto:legal@sensforge.com">contact us</a>.
          </p>
        </ContentSection>
        <ContentSection title="8. California Consumer Privacy Act Notice" className="mt-14">
          <p>
            California residents have additional privacy rights under the California Consumer Privacy Act, which are described in the California Consumer Privacy Act: https://oag.ca.gov/privacy/ccpa
          </p>
        </ContentSection>
        <ContentSection title="9. Updates to This Policy" className="mt-14">
          <p>
            We may update this policy periodically. Significant changes will be communicated via our website or other appropriate channels.
          </p>
        </ContentSection>
        <ContentSection title="10. Contact Us" className="mt-14">
          <HighlightSection>
            <HighlightText>
              For questions or concerns about this Privacy Policy, <br />
              please contact us at: <br />
              Email: legal@sensforge.com<br />
              Sensforge Inc.<br />
              639 Alpha Drive, Pittsburgh, PA 15238 <br />
            </HighlightText>
            <HighlightButton
              href="mailto:legal@sensforge.com"
              type="button"
            >
              Contact Us
            </HighlightButton>
          </HighlightSection>
        </ContentSection>
      </Container>
    </Layout>
  );
}
